import type { OrderListToolData } from '@/apis/assistant'

import type { OrderImportCardState } from './assistant-models'

export function getOrderImportCabinetIds(
  orders: OrderListToolData['orders'],
  orderId?: string,
  roomId?: string
): string[] {
  const ids: string[] = []
  for (const order of orders) {
    if (orderId && order.id !== orderId) continue
    for (const room of order.rooms) {
      if (roomId && room.id !== roomId) continue
      ids.push(room.id)
    }
  }
  return ids
}

function updateSelectedIds(
  selectedIds: string[],
  targetIds: string[],
  selected: boolean
): string[] {
  if (selected) return Array.from(new Set([...selectedIds, ...targetIds]))
  return selectedIds.filter((id) => !targetIds.includes(id))
}

export function createOrderImportCardState(
  orders: OrderListToolData['orders'] = [],
  dataId?: string
): OrderImportCardState {
  return {
    dataId,
    expandedOrderIds: orders.length ? [orders[0].id] : [],
    selectedCabinetIds: [],
  }
}

export function getSelectedOrderImportCount(
  state: OrderImportCardState
): number {
  return state.selectedCabinetIds.length
}

export function isOrderImportSelectionComplete(
  state: OrderImportCardState,
  cabinetIds: string[]
): boolean {
  return (
    cabinetIds.length > 0 &&
    cabinetIds.every((cabinetId) =>
      state.selectedCabinetIds.includes(cabinetId)
    )
  )
}

export function setOrderImportAllSelected(
  state: OrderImportCardState,
  orders: OrderListToolData['orders'],
  selected: boolean
): OrderImportCardState {
  return {
    ...state,
    selectedCabinetIds: selected ? getOrderImportCabinetIds(orders) : [],
  }
}

export function toggleOrderImportCabinet(
  state: OrderImportCardState,
  cabinetId: string
): OrderImportCardState {
  const selected = !state.selectedCabinetIds.includes(cabinetId)
  return {
    ...state,
    selectedCabinetIds: updateSelectedIds(
      state.selectedCabinetIds,
      [cabinetId],
      selected
    ),
  }
}

export function toggleOrderImportRoom(
  state: OrderImportCardState,
  orders: OrderListToolData['orders'],
  roomId: string
): OrderImportCardState {
  const cabinetIds = getOrderImportCabinetIds(orders, undefined, roomId)
  return {
    ...state,
    selectedCabinetIds: updateSelectedIds(
      state.selectedCabinetIds,
      cabinetIds,
      !isOrderImportSelectionComplete(state, cabinetIds)
    ),
  }
}

export function toggleOrderImportOrderSelection(
  state: OrderImportCardState,
  orders: OrderListToolData['orders'],
  orderId: string
): OrderImportCardState {
  const cabinetIds = getOrderImportCabinetIds(orders, orderId)
  return {
    ...state,
    selectedCabinetIds: updateSelectedIds(
      state.selectedCabinetIds,
      cabinetIds,
      !isOrderImportSelectionComplete(state, cabinetIds)
    ),
  }
}

export function toggleOrderImportOrder(
  state: OrderImportCardState,
  orderId: string
): OrderImportCardState {
  const expanded = state.expandedOrderIds.includes(orderId)
  return {
    ...state,
    expandedOrderIds: expanded
      ? state.expandedOrderIds.filter((id) => id !== orderId)
      : [...state.expandedOrderIds, orderId],
  }
}
